import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Waves } from "lucide-react";
import { LAYERS, MIXES } from "@/lib/sleepData";

const GOLD = "#EBD9A6";

/* four layers of real audio, built once on the first press */
function buildGraph(ctx) {
  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(ctx.destination);

  const noise = (seconds, brown) => {
    const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const w = Math.random() * 2 - 1;
      if (brown) { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; }
      else d[i] = w;
    }
    return buf;
  };
  const src = (buf) => { const s = ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.start(); return s; };
  const white = noise(3, false);
  const brown = noise(4, true);
  const gains = {};

  const attach = (k, node) => {
    const g = ctx.createGain();
    g.gain.value = 0;
    node.connect(g);
    g.connect(master);
    gains[k] = g;
  };

  // rain — high band of white noise, gently shimmering
  const rHp = ctx.createBiquadFilter(); rHp.type = "highpass"; rHp.frequency.value = 900;
  const rLp = ctx.createBiquadFilter(); rLp.type = "lowpass"; rLp.frequency.value = 7200;
  src(white).connect(rHp); rHp.connect(rLp);
  attach("rain", rLp);

  // wind — low band of brown noise with a slow sweep
  const wLp = ctx.createBiquadFilter(); wLp.type = "lowpass"; wLp.frequency.value = 480; wLp.Q.value = 2;
  src(brown).connect(wLp);
  const wLfo = ctx.createOscillator(); wLfo.frequency.value = 0.055;
  const wDepth = ctx.createGain(); wDepth.gain.value = 260;
  wLfo.connect(wDepth); wDepth.connect(wLp.frequency); wLfo.start();
  attach("wind", wLp);

  // embers — mid brown noise with an irregular tremolo
  const eBp = ctx.createBiquadFilter(); eBp.type = "bandpass"; eBp.frequency.value = 1500; eBp.Q.value = 0.7;
  src(white).connect(eBp);
  const trem = ctx.createGain(); trem.gain.value = 0.55;
  eBp.connect(trem);
  const eLfo = ctx.createOscillator(); eLfo.type = "triangle"; eLfo.frequency.value = 1.7;
  const eDepth = ctx.createGain(); eDepth.gain.value = 0.42;
  eLfo.connect(eDepth); eDepth.connect(trem.gain); eLfo.start();
  attach("embers", trem);

  // hum — two detuned low sines
  const hMix = ctx.createGain(); hMix.gain.value = 0.5;
  [68, 102.5].forEach((f) => {
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f; o.start();
    const g = ctx.createGain(); g.gain.value = f > 90 ? 0.28 : 0.5;
    o.connect(g); g.connect(hMix);
  });
  attach("hum", hMix);

  return { master, gains };
}

export default function Mixer() {
  const [levels, setLevels] = useState({ rain: 0.55, wind: 0.2, embers: 0, hum: 0.12 });
  const [on, setOn] = useState(false);
  const [preset, setPreset] = useState(null);
  const ctxRef = useRef(null);
  const graphRef = useRef(null);

  const start = async () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      graphRef.current = buildGraph(ctxRef.current);
    }
    await ctxRef.current.resume();
    setOn(true);
  };
  const stop = () => { ctxRef.current?.suspend(); setOn(false); };

  useEffect(() => {
    const g = graphRef.current;
    if (!g || !ctxRef.current) return;
    const t = ctxRef.current.currentTime;
    Object.entries(levels).forEach(([k, v]) => {
      const node = g.gains[k];
      if (node) node.gain.setTargetAtTime(on ? v * 0.42 : 0, t, 0.25);
    });
  }, [levels, on]);

  useEffect(() => () => { ctxRef.current?.close?.(); }, []);

  const setOne = (k, v) => { setLevels((l) => ({ ...l, [k]: v })); setPreset(null); };
  const applyPreset = (m, i) => { setLevels(m.levels); setPreset(i); if (!on) start(); };

  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #131838, #0C1029 72%)" }} data-testid="sleep-mixer">
      <span className="absolute right-[10%] -top-32 w-[34vw] h-[34vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(107,116,220,0.4), transparent 68%)", filter: "blur(46px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(235,217,166,0.14)", color: GOLD }}>
            <Waves size={11} /> real sound, built in the browser
          </span>
          <h2 className="font-display text-[34px] sm:text-[46px] leading-[1.02] mt-4 text-white">Four layers, and a hand on each one</h2>
          <p className="text-[15px] mt-4 text-white/50 max-w-sm">No files, no streaming, nothing loading. The sound is generated as you move the faders, so it never loops and never ends.</p>

          <button onClick={() => (on ? stop() : start())} className="mt-8 inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            style={on ? { background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" } : { background: GOLD, color: "#171B3C", boxShadow: "0 22px 46px -22px rgba(235,217,166,0.7)" }}
            data-testid="mixer-toggle">
            {on ? <><Pause size={15} /> Stop the room</> : <><Play size={15} /> Turn it on</>}
          </button>

          <div className="flex flex-wrap gap-2 mt-6">
            {MIXES.map((m, i) => (
              <button key={m.name} onClick={() => applyPreset(m, i)} className="rounded-full px-4 py-2 text-[12px] transition-colors"
                style={preset === i ? { background: "rgba(235,217,166,0.2)", color: GOLD, border: "1px solid rgba(235,217,166,0.45)" } : { background: "rgba(255,255,255,0.055)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.09)" }}
                data-testid={`mixer-preset-${i}`}>{m.name}</button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 rounded-[2rem] p-6 sm:p-8" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(14px)" }}>
          <div className="space-y-6">
            {LAYERS.map((l) => (
              <div key={l.k} data-testid={`mixer-layer-${l.k}`}>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="font-display text-[19px] text-white leading-none">{l.label}</p>
                    <p className="text-[11.5px] mt-1 text-white/40">{l.hint}</p>
                  </div>
                  <div className="flex items-end gap-[3px] h-8">
                    {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                      <motion.span key={n} className="w-[3px] rounded-full" style={{ background: l.tone, opacity: 0.75 }}
                        animate={on && levels[l.k] > 0.02 ? { height: [`${18 + n * 5}%`, `${45 + ((n * 13) % 55)}%`, `${18 + n * 5}%`] } : { height: "8%" }}
                        transition={{ duration: 1.3 + (n % 3) * 0.5, repeat: Infinity, ease: "easeInOut" }} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <input type="range" min={0} max={1} step={0.01} value={levels[l.k]} onChange={(e) => setOne(l.k, Number(e.target.value))}
                    className="flex-1" style={{ accentColor: l.tone }} aria-label={l.label} data-testid={`mixer-fader-${l.k}`} />
                  <span className="w-9 text-right text-[12px] tabular-nums text-white/45">{Math.round(levels[l.k] * 100)}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11.5px] mt-7 text-white/30">Headphones are not required and are not ideal. Put it on a speaker across the room, quiet enough that you stop noticing it.</p>
        </div>
      </div>
    </section>
  );
}
